import React, { useState, useEffect } from 'react';
import { useLang } from '../../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import IntegrationSetupWizard from './IntegrationSetupWizard';
import IntegrationEditModal from './IntegrationEditModal';
import { Plug, Plus, RefreshCw, Circle, Pencil, Trash2, Play } from 'lucide-react';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS } from '../../styles/emziCI';
import { secureStorage } from '../../utils/secureStorage';
import toast from '../../utils/toast';
import { BACKEND_URL } from '../../utils/backendUrl';

const IntegrationCenterCard = () => {
  const t = useLang();
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [testingId, setTestingId] = useState(null);

  useEffect(() => {
    fetchIntegrations();
  }, []);

  const fetchIntegrations = async () => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setIntegrations(data);
      }
    } catch (error) {
      console.error('Error fetching integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (integrationId) => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/${integrationId}`,
        { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        toast.success(t('admin.integrations.deleted') || 'Integration deleted');
        setDeletingId(null);
        fetchIntegrations();
      } else {
        toast.error(t('admin.integrations.deleteFailed') || 'Failed to delete');
      }
    } catch (error) {
      toast.error(t('admin.error.networkError') || 'Network error');
    }
  };

  const handleTestExisting = async (integrationId) => {
    setTestingId(integrationId);
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/${integrationId}/test`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const result = await response.json();
      if (result.success) {
        toast.success(result.message || 'Connection successful');
      } else {
        toast.error(result.message || 'Connection failed');
      }
      fetchIntegrations();
    } catch (error) {
      toast.error(t('admin.error.networkError') || 'Network error');
    } finally {
      setTestingId(null);
    }
  };

  const handleEdit = async (integration) => {
    try {
      const token = secureStorage.getItem('nexus_access_token');
      const response = await fetch(
        `${BACKEND_URL}/api/admin-panel/integrations/${integration.integration_id}/config`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setEditingIntegration(data);
      } else {
        toast.error('Failed to load integration config');
      }
    } catch (error) {
      toast.error(t('admin.error.networkError') || 'Network error');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return { bg: '#F0FDF4', text: EMZI_COLORS.success, border: `${EMZI_COLORS.success}20` };
      case 'disconnected':
        return { bg: '#FEF2F2', text: EMZI_COLORS.error, border: `${EMZI_COLORS.error}20` };
      default:
        return { bg: '#F3F4F6', text: EMZI_COLORS.textSecondary, border: `${EMZI_COLORS.textSecondary}20` };
    }
  };

  return (
    <>
      <Card
        className="hover:shadow-lg transition-shadow border-0"
        style={{ boxShadow: EMZI_SHADOWS.card, borderRadius: EMZI_RADIUS.md }}
      >
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Plug size={24} strokeWidth={2} style={{ color: EMZI_COLORS.accent }} />
            <div>
              <div style={{ color: EMZI_COLORS.primary, fontWeight: '700' }} data-testid="integration-center-title">
                {t('admin.integrations.title')}
              </div>
              <div className="text-xs font-normal text-gray-600 mt-1">
                {t('admin.integrations.subtitle')}
              </div>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-2">
            <div
              className="rounded-lg p-3"
              style={{
                backgroundColor: `${EMZI_COLORS.accent}15`,
                border: `1px solid ${EMZI_COLORS.accent}30`,
                borderRadius: EMZI_RADIUS.sm,
              }}
            >
              <div className="text-2xl font-bold" style={{ color: EMZI_COLORS.accent }} data-testid="integration-total-count">
                {loading ? '...' : integrations.length}
              </div>
              <div className="text-xs mt-1" style={{ color: EMZI_COLORS.textSecondary }}>
                {t('admin.integrations.total')}
              </div>
            </div>
            <div
              className="rounded-lg p-3"
              style={{
                backgroundColor: '#F0FDF4',
                border: `1px solid ${EMZI_COLORS.success}20`,
                borderRadius: EMZI_RADIUS.sm,
              }}
            >
              <div className="text-2xl font-bold" style={{ color: EMZI_COLORS.success }} data-testid="integration-connected-count">
                {loading ? '...' : integrations.filter(i => i.status === 'connected').length}
              </div>
              <div className="text-xs mt-1" style={{ color: EMZI_COLORS.textSecondary }}>
                {t('admin.integrations.connected')}
              </div>
            </div>
          </div>

          {/* Integrations List */}
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {loading ? (
              <div className="text-center py-4 text-gray-500 text-sm">
                {t('common.loading')}...
              </div>
            ) : integrations.length === 0 ? (
              <div className="text-center py-4 text-gray-400 text-sm" data-testid="no-integrations-message">
                {t('admin.integrations.noIntegrations')}
              </div>
            ) : (
              integrations.map((integration) => {
                const statusColors = getStatusColor(integration.status);
                const isDeleting = deletingId === integration.integration_id;
                const isTesting = testingId === integration.integration_id;

                return (
                  <div
                    key={integration.integration_id}
                    className="bg-white rounded-lg p-3 hover:border-orange-300 transition-colors"
                    style={{
                      border: `1px solid ${EMZI_COLORS.borderLight}`,
                      borderRadius: EMZI_RADIUS.sm,
                    }}
                    data-testid={`integration-item-${integration.integration_id}`}
                  >
                    {/* Delete confirmation inline */}
                    {isDeleting ? (
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-red-700">
                          {t('admin.integrations.confirmDelete') || 'Delete this integration?'}
                        </p>
                        <p className="text-xs text-gray-500">
                          {integration.integration_type.replace('_', ' ').toUpperCase()}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleDelete(integration.integration_id)}
                            className="flex-1 py-1.5 rounded text-xs font-semibold text-white transition-colors"
                            style={{ backgroundColor: EMZI_COLORS.error }}
                            data-testid={`confirm-delete-${integration.integration_id}`}
                          >
                            {t('common.confirm') || 'Confirm'}
                          </button>
                          <button
                            onClick={() => setDeletingId(null)}
                            className="flex-1 py-1.5 rounded text-xs font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
                            data-testid={`cancel-delete-${integration.integration_id}`}
                          >
                            {t('common.cancel') || 'Cancel'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm" style={{ color: EMZI_COLORS.textPrimary }}>
                              {integration.integration_type.replace('_', ' ').toUpperCase()}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              {integration.last_tested ? (
                                <span>Last tested: {new Date(integration.last_tested).toLocaleDateString()}</span>
                              ) : (
                                <span>{t('admin.integrations.notTested')}</span>
                              )}
                            </div>
                            {integration.config_summary && Object.keys(integration.config_summary).length > 1 && (
                              <div className="text-xs text-gray-400 mt-0.5 truncate">
                                {Object.entries(integration.config_summary)
                                  .filter(([k]) => k !== 'type')
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(' | ')}
                              </div>
                            )}
                          </div>
                          <div
                            className="px-2 py-1 rounded text-xs flex items-center gap-1 shrink-0 ml-2"
                            style={{
                              backgroundColor: statusColors.bg,
                              color: statusColors.text,
                              border: `1px solid ${statusColors.border}`,
                              borderRadius: EMZI_RADIUS.sm,
                            }}
                            data-testid={`integration-status-${integration.integration_id}`}
                          >
                            <Circle
                              size={10}
                              fill={statusColors.text}
                              stroke={statusColors.text}
                            />
                            <span>{integration.status}</span>
                          </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-1.5 mt-2 pt-2 border-t border-gray-100">
                          <button
                            onClick={() => handleTestExisting(integration.integration_id)}
                            disabled={isTesting}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors hover:bg-blue-50 text-blue-600 border border-blue-200"
                            data-testid={`test-integration-${integration.integration_id}`}
                          >
                            {isTesting ? (
                              <RefreshCw size={12} className="animate-spin" />
                            ) : (
                              <Play size={12} />
                            )}
                            {isTesting ? 'Testing...' : 'Test'}
                          </button>
                          <button
                            onClick={() => handleEdit(integration)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors hover:bg-orange-50 text-orange-600 border border-orange-200"
                            data-testid={`edit-integration-${integration.integration_id}`}
                          >
                            <Pencil size={12} />
                            {t('common.edit') || 'Edit'}
                          </button>
                          <button
                            onClick={() => setDeletingId(integration.integration_id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium transition-colors hover:bg-red-50 text-red-600 border border-red-200"
                            data-testid={`delete-integration-${integration.integration_id}`}
                          >
                            <Trash2 size={12} />
                            {t('common.delete') || 'Delete'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2 pt-2">
            <button
              onClick={() => setShowWizard(true)}
              className="py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: EMZI_COLORS.accent,
                color: '#FFFFFF',
                borderRadius: EMZI_RADIUS.sm,
                minHeight: '44px',
              }}
              data-testid="integration-setup-btn"
            >
              <Plus size={16} strokeWidth={2} />
              {t('admin.integrations.setup')}
            </button>
            <button
              onClick={fetchIntegrations}
              className="py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 hover:opacity-90"
              style={{
                backgroundColor: '#F3F4F6',
                color: EMZI_COLORS.textSecondary,
                borderRadius: EMZI_RADIUS.sm,
                minHeight: '44px',
              }}
              data-testid="integration-refresh-btn"
            >
              <RefreshCw size={16} strokeWidth={2} />
              {t('common.refresh')}
            </button>
          </div>
        </CardContent>
      </Card>

      {showWizard && (
        <IntegrationSetupWizard
          onClose={() => setShowWizard(false)}
          onSuccess={() => {
            setShowWizard(false);
            fetchIntegrations();
          }}
        />
      )}

      {editingIntegration && (
        <IntegrationEditModal
          integration={editingIntegration}
          onClose={() => setEditingIntegration(null)}
          onSuccess={() => {
            setEditingIntegration(null);
            fetchIntegrations();
          }}
        />
      )}
    </>
  );
};

export default IntegrationCenterCard;
